import React, { useState } from 'react';
import { Minus, Plus, ArrowRight, Check } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWoo } from '../../context/WooContext';
import { FLAVOUR_ORDER, FLAVOURS } from '../../data/mock';
import Reveal from '../../components/Reveal';
import { track } from '../../lib/analytics';

const AVAIL = FLAVOUR_ORDER.filter((id) => FLAVOURS[id].available !== false && FLAVOURS[id].woo);
const TARGET = AVAIL.length;

export default function MixBox() {
  const { addItem } = useCart();
  const { priceOf, money } = useWoo();
  const [mode, setMode] = useState('preset');
  const [qty, setQty] = useState(() => AVAIL.reduce((o, id) => ({ ...o, [id]: 1 }), {}));
  const total = AVAIL.reduce((s, id) => s + (qty[id] || 0), 0);
  const complete = total === TARGET;

  const unitOf = (id) => priceOf(FLAVOURS[id].woo) || 0;
  const presetPrice = AVAIL.reduce((s, id) => s + unitOf(id), 0);
  const customPrice = AVAIL.reduce((s, id) => s + (qty[id] || 0) * unitOf(id), 0);

  const change = (id, d) => {
    setQty((q) => {
      const nextVal = Math.max(0, (q[id] || 0) + d);
      const others = AVAIL.reduce((s, k) => s + (k === id ? nextVal : (q[k] || 0)), 0);
      if (others > TARGET) return q;
      if (AVAIL.every((v) => (q[v] || 0) === 1) && d < 0) track('mix_box_started', {});
      return { ...q, [id]: nextVal };
    });
  };

  const addPreset = () => {
    const wooItems = AVAIL.map((id) => ({ id: FLAVOURS[id].woo, quantity: 1 }));
    addItem({ key: 'mix-preset', title: `S-MAN MIX — ${TARGET} FLAVOURS`, flavour: 'mix', price: presetPrice, qty: 1, boxes: TARGET, type: 'mix', color: '#ff2d2d', rgb: '255,45,45', wooItems });
    track('mix_box_completed', { preset: true });
  };
  const addCustom = () => {
    if (!complete) return;
    const wooItems = AVAIL.filter((id) => qty[id]).map((id) => ({ id: FLAVOURS[id].woo, quantity: qty[id] }));
    addItem({ key: `mix-custom-${Date.now()}`, title: `Custom Mix (${AVAIL.filter((id) => qty[id]).map((id) => `${qty[id]}x ${FLAVOURS[id].name}`).join(', ')})`, flavour: 'mix', price: customPrice, qty: 1, boxes: TARGET, type: 'mix', color: '#ff2d2d', rgb: '255,45,45', wooItems });
    track('mix_box_completed', { preset: false, qty });
  };

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(255,45,45,0.12), transparent 60%), #050505' }} />
      <div className="max-w-[1400px] mx-auto px-5">
        <Reveal className="text-center mb-4">
          <h2 className="font-display text-5xl md:text-7xl">CAN'T CHOOSE?<br /><span className="text-[#ff2d2d]">DON'T.</span></h2>
        </Reveal>
        <Reveal className="text-center mb-10"><p className="font-cond tracking-[0.2em] text-white/50">THE S-MAN MIX · {TARGET} FLAVOURS. ONE EXPERIENCE.</p></Reveal>

        {/* product row (all flavours; unavailable shown as coming soon) */}
        <div className="flex items-center justify-center gap-2 md:gap-6 mb-10 flex-wrap">
          {FLAVOUR_ORDER.map((id) => {
            const soon = FLAVOURS[id].available === false;
            return (
              <div key={id} className="flex flex-col items-center gap-2" style={{ opacity: soon ? 0.4 : 1 }}>
                <img src={FLAVOURS[id].fruit} alt={FLAVOURS[id].name} loading="lazy" className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2" style={{ borderColor: FLAVOURS[id].primary }} />
                <span className="font-cond text-xs" style={{ color: FLAVOURS[id].primary }}>{FLAVOURS[id].name}</span>
                {soon && <span className="text-[9px] font-cond tracking-wide text-white/40">FROM {FLAVOURS[id].comingSoon}</span>}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-3 mb-8">
          <button onClick={() => setMode('preset')} className={`px-5 py-2.5 rounded-full font-cond tracking-wide text-sm transition-all ${mode === 'preset' ? 'bg-[#ff2d2d] text-white' : 'border border-white/15 text-white/60'}`}>THE S-MAN MIX</button>
          <button onClick={() => setMode('build')} className={`px-5 py-2.5 rounded-full font-cond tracking-wide text-sm transition-all ${mode === 'build' ? 'bg-[#ff2d2d] text-white' : 'border border-white/15 text-white/60'}`}>BUILD YOUR OWN MIX</button>
        </div>

        <div className="max-w-2xl mx-auto rounded-3xl border border-white/10 bg-[#0b0b0b] p-6 md:p-10">
          {mode === 'preset' ? (
            <div className="text-center">
              <p className="text-white/60 mb-2">One box of every available flavour. The complete S-MAN experience.</p>
              <div className="flex items-center justify-center gap-3 my-6">
                <span className="font-display text-5xl text-[#ff2d2d]">{money(presetPrice)}</span>
              </div>
              <button data-testid="mix-add-preset" onClick={addPreset} className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#ff2d2d] text-white font-cond tracking-wide text-lg hover:brightness-110 transition-all">GET THE MIX BOX <ArrowRight className="w-5 h-5" /></button>
            </div>
          ) : (
            <div>
              {AVAIL.map((id) => (
                <div key={id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <img src={FLAVOURS[id].fruit} alt={FLAVOURS[id].name} className="w-9 h-9 rounded-full object-cover border" style={{ borderColor: FLAVOURS[id].primary }} loading="lazy" />
                    <span className="font-cond tracking-wide" style={{ color: FLAVOURS[id].primary }}>{FLAVOURS[id].name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => change(id, -1)} className="w-8 h-8 rounded-md border border-white/15 flex items-center justify-center hover:bg-white/10"><Minus className="w-4 h-4" /></button>
                    <span className="w-6 text-center font-cond text-lg">{qty[id] || 0}</span>
                    <button onClick={() => change(id, 1)} disabled={total >= TARGET} className="w-8 h-8 rounded-md border border-white/15 flex items-center justify-center hover:bg-white/10 disabled:opacity-30"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between mt-6">
                <span className="font-cond tracking-wide text-lg" style={{ color: complete ? '#7ED321' : '#ff2d2d' }}>{total} / {TARGET} SELECTED {complete && <Check className="inline w-5 h-5" />}</span>
                <span className="font-display text-3xl">{money(customPrice)}</span>
              </div>
              <button data-testid="mix-add-custom" onClick={addCustom} disabled={!complete} className="w-full mt-5 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-[#ff2d2d] text-white font-cond tracking-wide text-lg hover:brightness-110 transition-all disabled:opacity-30">ADD MY MIX <ArrowRight className="w-5 h-5" /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
